import { Injectable } from "@angular/core";
import { DbDaoBase } from "../../../gyzmo-commons/dao/db/base/db.dao.base";
import { IdWordingDbDao } from "../../../gyzmo-commons/dao/db/idWording.db.dao";
import { AppSqlProvider } from "../../../gyzmo-commons/persistence/app.sql.provider";
import { LoggerService } from "../../../gyzmo-commons/services/logs/logger.service";
import { User } from "../../models/user.model";
import { LocationDbDao } from "./location.db.dao";
import { ThirdPartyDbDao } from "./thirdParty.db.dao";

@Injectable({
    providedIn: "root",
})
export class UserDbDao extends DbDaoBase<User> {
    constructor(logger: LoggerService,
                private sqlProvider: AppSqlProvider,
                private idWordingDbDao: IdWordingDbDao,
                private thirdPartyDbDao: ThirdPartyDbDao,
                private locationDbDao: LocationDbDao) {
        super(logger);
    }

    public getCurrentUserWithThirdPartyOnly(): Promise<User> {
        return this.get(null, false)
            .then(user => {
                if (user) {
                    return this.thirdPartyDbDao.get(user.thirdParty.id, false)
                        .then(thirdParty => {
                            user.thirdParty = thirdParty;
                            return user;
                        });
                } else {
                    return null;
                }
            });
    }

    public async createIndexes(): Promise<void> {
        // Implémenter la création des index si nécessaire
    }

    public createTable(): Promise<void> {
        const query = `
            CREATE TABLE IF NOT EXISTS ${ User.TABLENAME } (
                id TEXT PRIMARY KEY,
                username TEXT,
                thirdParty TEXT,
                location TEXT,
                sector TEXT,
                wording TEXT,
                language TEXT,
                company TEXT,
                profile TEXT
            );
        `;
        const params: any[] = [];
        return this.sqlProvider.query(query, params)
            .then(async () => {
                await this.createIndexes();
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public delete(id: string): Promise<any> {
        const query = `DELETE FROM ${ User.TABLENAME } WHERE id = ?;`;
        const params = [id];
        return this.sqlProvider.query(query, params);
    }

    public deleteAll(): Promise<any> {
        const query = `DELETE FROM ${ User.TABLENAME };`;
        const params: any[] = [];
        return this.sqlProvider.query(query, params);
    }

    public get(id: string, hydrate: boolean): Promise<User> {
        let query: string;
        let params: any[];
        if (id) {
            query = `SELECT * FROM ${ User.TABLENAME } WHERE id = ?;`;
            params = [id];
        } else {
            query = `SELECT * FROM ${ User.TABLENAME };`;
            params = [];
        }
        return this.sqlProvider.query(query, params)
            .then(data => {
                if (data.rows.length <= 0) {
                    return null;
                }
                const user: User = this.rowToModel(data.rows[0]);
                const hydratationPromises: Promise<any>[] = [];
                if (hydrate) {
                    hydratationPromises.push(this.thirdPartyDbDao.get(user.thirdParty.id, hydrate)
                        .then(value => {
                            user.thirdParty = value;
                        }));
                    hydratationPromises.push(this.locationDbDao.get(user.location.id, hydrate)
                        .then(value => {
                            user.location = value;
                        }));
                    hydratationPromises.push(this.idWordingDbDao.get(user.company.id, hydrate)
                        .then(value => {
                            user.company = value;
                        }));
                    hydratationPromises.push(this.idWordingDbDao.get(user.profile.id, hydrate)
                        .then(value => {
                            user.profile = value;
                        }));
                    hydratationPromises.push(this.idWordingDbDao.get(user.sector.id, hydrate)
                        .then(value => {
                            user.sector = value;
                        }));
                }
                return Promise.all(hydratationPromises)
                    .then(() => {
                        return user;
                    });
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public getTableName(): string {
        return User.TABLENAME;
    }

    protected rowToModel(row: any): User {
        const user = new User();
        user.id = row.id;
        user.username = row.username;
        user.wording = row.wording;
        user.language = row.language;
        user.thirdParty.id = row.thirdParty;
        user.location.id = row.location;
        user.company.id = row.company;
        user.profile.id = row.profile;
        user.sector.id = row.sector;
        return user;
    }

    public save(user: User): Promise<User> {
        const promises: Promise<any>[] = [];
        if (user.thirdParty) {
            promises.push(this.thirdPartyDbDao.save(user.thirdParty));
        }
        if (user.location) {
            promises.push(this.locationDbDao.save(user.location));
        }
        if (user.company) {
            promises.push(this.idWordingDbDao.save(user.company));
        }
        if (user.profile) {
            promises.push(this.idWordingDbDao.save(user.profile));
        }
        if (user.sector) {
            promises.push(this.idWordingDbDao.save(user.sector));
        }
        return Promise.all(promises)
            .then(() => {
                const query = `
                    INSERT OR REPLACE INTO ${ User.TABLENAME }
                    (id, username, thirdParty, location, sector, language, wording, company, profile)
                    VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?);
                `;
                const params = [
                    user.id,
                    user.username,
                    user.thirdParty ? user.thirdParty.id : null,
                    user.location ? user.location.id : null,
                    user.sector ? user.sector.id : null,
                    user.language,
                    user.wording,
                    user.company ? user.company.id : null,
                    user.profile ? user.profile.id : null,
                ];
                return this.sqlProvider.query(query, params)
                    .then(response => {
                        return user;
                    })
                    .catch(reason => {
                        this.logSqlError(reason);
                        return null;
                    });
            });
    }
}
